import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Modal from 'react-modal'

import { getUserConsents, postConsents, postFamilyConsents } from '../../actions/consentActions'
import { updateCurrentUser, updateUser } from '../../actions/userActions'
import ConsentContainer from './ConsentContainer'

const isConsentRequired = (consents) => {
  if (!consents || Array.isArray(consents)) { return false }
  if (consents.age_needed || consents.contact_support || consents.needs_proxy) { return true }
  if (consents.phone_needed) { return true }
  if (consents.family_ages_needed?.length > 0) { return true }
  if (consents.family_consents_needed?.length > 0) { return true }

  const personalDocs = [consents.privacy_policy, consents.eula, consents.saas_agreement]
  return personalDocs.some((doc) => doc && !doc.consented)
}

const computeConsentType = (consents) => {
  if (consents?.age_needed) { return 'userAge' }
  if (consents?.contact_support) { return 'contactSupport' }
  if (consents?.needs_proxy) { return 'needsProxy' }
}

const GlobalConsentModal = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const location = useLocation()
  const user = useSelector((state) => state.user.current_user)
  const isImpersonating = useSelector((state) => state.user.is_impersonating)
  const consents = useSelector((state) => state.consents)

  const [showConsentModal, setShowConsentModal] = useState(false)
  const [consentType, setConsentType] = useState(null)
  const fetchedForUserId = useRef(null)

  const userId = user?.id
  const isLoggingOut = location.pathname === '/post-logout'

  useEffect(() => {
    if (!userId || isLoggingOut || isImpersonating) {
      fetchedForUserId.current = null
      return
    }
    if (fetchedForUserId.current === userId) { return }

    fetchedForUserId.current = userId
    dispatch(getUserConsents(userId))
  }, [userId, isLoggingOut, isImpersonating, dispatch])

  useEffect(() => {
    setShowConsentModal(isConsentRequired(consents))
    setConsentType(computeConsentType(consents))
  }, [consents])

  if (!userId || isLoggingOut || isImpersonating) { return null }

  const refreshConsents = () => dispatch(getUserConsents(userId))
  const closeConsentModal = () => setShowConsentModal(false)

  const submitOwnBirthdate = (birthdate) =>
    dispatch(updateCurrentUser({ id: userId, birthdate })).finally(refreshConsents)

  const submitOwnPhone = ({ phone, phonePrefix, phonePrefixCountry, promotionalTextMessages }) =>
    dispatch(updateCurrentUser({
      id: userId,
      phone,
      phone_prefix: phonePrefix,
      phone_prefix_country: phonePrefixCountry,
      promotional_text_messages: promotionalTextMessages,
    })).finally(refreshConsents)

  const submitFamilyBirthdates = (birthdates) =>
    Promise.all(birthdates.map((u) => dispatch(updateUser(u)))).finally(refreshConsents)

  const submitFamilyConsents = () =>
    dispatch(postFamilyConsents()).finally(refreshConsents)

  const submitConsents = (consentArray) =>
    dispatch(postConsents(consentArray, userId))

  return (
    <Modal isOpen={showConsentModal} contentLabel={t('consent.globalModal.contentLabel')} className='customModal'>
      <ConsentContainer
        currentUser={user}
        consentType={consentType}
        consents={consents}
        submitOwnBirthdate={submitOwnBirthdate}
        submitOwnPhone={submitOwnPhone}
        submitFamilyBirthdates={submitFamilyBirthdates}
        submitConsents={submitConsents}
        submitFamilyConsents={submitFamilyConsents}
        closeModal={closeConsentModal}
      />
    </Modal>
  )
}

export default GlobalConsentModal
